import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  BarChart3,
  BookOpen,
  Briefcase,
  Heart,
  Radio,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Users,
  WalletCards,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const dateValue = (date) => date.toISOString().slice(0, 10);
const number = (value) => new Intl.NumberFormat("es-CO").format(Number(value) || 0);
const today = new Date();
const initialFrom = new Date(today.getTime() - 30 * 86400000);

const Metric = ({ icon: Icon, label, value, suffix = "", alert = false }) => (
  <article className="kd9-card p-5">
    <Icon size={19} style={{ color: alert ? "#FCA5A5" : "var(--kd9-gold)" }} />
    <div className="mt-3 font-cinzel text-3xl" style={{ color: alert ? "#FCA5A5" : "var(--kd9-gold)" }}>
      {number(value)}{suffix}
    </div>
    <div className="mt-1 text-[10px] uppercase tracking-widest text-white/55">{label}</div>
  </article>
);

const Breakdown = ({ title, values }) => (
  <section className="kd9-card p-5 min-w-0">
    <h2 className="font-cinzel kd9-gold-text">{title}</h2>
    <div className="mt-4 space-y-2">
      {Object.entries(values || {}).sort((a, b) => b[1] - a[1]).map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-3 border-b border-white/10 pb-2 text-sm">
          <span className="capitalize truncate">{label.replaceAll("_", " ")}</span>
          <b className="kd9-gold-text">{number(value)}</b>
        </div>
      ))}
      {!Object.keys(values || {}).length && <p className="text-sm text-white/45">Sin actividad en el período.</p>}
    </div>
  </section>
);

const OperationCard = ({ icon: Icon, title, primary, primaryLabel, energy, statuses }) => (
  <section className="kd9-card p-5">
    <div className="flex items-center gap-3">
      <Icon size={18} className="kd9-gold-text" />
      <h3 className="font-cinzel kd9-gold-text">{title}</h3>
    </div>
    <div className="grid grid-cols-2 gap-3 mt-4">
      <div>
        <div className="font-cinzel text-2xl">{number(primary)}</div>
        <div className="text-[9px] uppercase tracking-widest text-white/45">{primaryLabel}</div>
      </div>
      <div>
        <div className="font-cinzel text-2xl">{number(energy)} KD</div>
        <div className="text-[9px] uppercase tracking-widest text-white/45">Energía registrada</div>
      </div>
    </div>
    <div className="mt-4 space-y-1">
      {Object.entries(statuses || {}).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([status, value]) => (
        <div key={status} className="flex justify-between text-xs border-t border-white/10 pt-2 mt-2">
          <span className="capitalize text-white/60">{status.replaceAll("_", " ")}</span>
          <b>{number(value)}</b>
        </div>
      ))}
      {!Object.keys(statuses || {}).length && <div className="text-xs text-white/45">Sin movimientos.</div>}
    </div>
  </section>
);

export default function GlobalReports() {
  const [range, setRange] = useState({ from: dateValue(initialFrom), to: dateValue(today) });
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const params = useCallback(() => ({
    date_from: new Date(range.from + "T00:00:00.000Z").toISOString(),
    date_to: new Date(range.to + "T23:59:59.999Z").toISOString(),
  }), [range]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/api/director/reports/global", { params: params() });
      setReport(data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const maxDaily = useMemo(() => Math.max(
    1,
    ...(report?.activity_daily || []).map((row) =>
      Number(row.users || 0)
      + Number(row.diagnostics || 0)
      + Number(row.academy || 0)
      + Number(row.marketplace || 0)
      + Number(row.accompaniment || 0)
      + Number(row.consultant_services || 0)
    ),
  ), [report]);

  const r = report || {};
  const ops = r.operations || {};
  const governanceTotal =
    Number(r.governance?.pending_role_requests || 0)
    + Number(r.governance?.pending_products || 0)
    + Number(r.governance?.diagnostics_final_review || 0)
    + Number(r.governance?.pending_withdrawals || 0);

  return (
    <PageShell
      kicker="Director KD9 · Inteligencia de gobierno"
      title="ANALÍTICA GLOBAL KD9"
      desc="Una sola lectura ejecutiva del ecosistema: actividad real, Energía KD, operación, contenido y decisiones pendientes."
      testId="global-analytics-page"
      badge={
        <div className="flex gap-2">
          <Link to="/app/auditoria" className="kd9-btn-outline rounded-lg px-3 py-2 text-[10px]">
            <ShieldCheck size={13} className="inline" /> Auditoría
          </Link>
          <button disabled={busy} onClick={load} className="kd9-btn-outline rounded-lg p-2" aria-label="Actualizar">
            <RefreshCw size={16} />
          </button>
        </div>
      }
    >
      <div className="kd9-card mb-5 flex flex-wrap items-end gap-3 p-4" data-testid="analytics-period">
        <label className="text-xs">
          Desde
          <input
            type="date"
            className="kd9-input mt-1 block p-3"
            value={range.from}
            max={range.to}
            onChange={(e) => setRange({ ...range, from: e.target.value })}
          />
        </label>
        <label className="text-xs">
          Hasta
          <input
            type="date"
            className="kd9-input mt-1 block p-3"
            value={range.to}
            min={range.from}
            max={dateValue(today)}
            onChange={(e) => setRange({ ...range, to: e.target.value })}
          />
        </label>
        <button disabled={busy} onClick={load} className="kd9-btn-primary px-4 py-3">
          {busy ? "Calculando…" : "Aplicar período"}
        </button>
        {report?.generated_at && (
          <span className="ml-auto text-[10px] text-white/40">
            Actualizado {new Date(report.generated_at).toLocaleString("es-CO")}
          </span>
        )}
      </div>

      {error && <div className="kd9-card mb-5 p-4 text-sm text-red-300">{error}</div>}

      {!report ? (
        <div className="kd9-card p-8">Calculando indicadores globales…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 mb-6" data-testid="analytics-summary">
            <Metric icon={Users} label="Usuarios activos" value={r.users?.active} />
            <Metric icon={WalletCards} label="Energía en wallets" value={r.energy?.balance} suffix=" KD" />
            <Metric icon={Sparkles} label="Diagnósticos del período" value={ops.diagnostics?.count} />
            <Metric icon={BookOpen} label="Matrículas del período" value={ops.academy?.enrollments} />
            <Metric icon={ShoppingBag} label="Pedidos Marketplace" value={ops.marketplace?.orders} />
            <Metric icon={Radio} label="Sesiones Templo Vivo" value={r.live?.sessions} />
            <Metric icon={Activity} label="Movimientos Energía" value={r.energy?.movements} />
            <Metric
              icon={governanceTotal > 0 ? ShieldAlert : ShieldCheck}
              label="Decisiones pendientes"
              value={governanceTotal}
              alert={governanceTotal > 0}
            />
          </div>

          <section className="kd9-card p-5 mb-6" data-testid="analytics-activity">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="font-cinzel kd9-gold-text text-lg">PULSO DE ACTIVIDAD</h2>
                <p className="text-xs text-white/45">Usuarios nuevos + actividad operativa diaria de los principales módulos.</p>
              </div>
              <BarChart3 size={20} className="kd9-gold-text" />
            </div>
            <div className="h-52 flex items-end gap-[2px] overflow-x-auto border-b border-white/10">
              {(r.activity_daily || []).map((row) => {
                const total =
                  Number(row.users || 0)
                  + Number(row.diagnostics || 0)
                  + Number(row.academy || 0)
                  + Number(row.marketplace || 0)
                  + Number(row.accompaniment || 0)
                  + Number(row.consultant_services || 0);
                return (
                  <div
                    key={row.date}
                    className="min-w-[5px] flex-1 rounded-t"
                    style={{
                      height: `${Math.max(2, (total / maxDaily) * 100)}%`,
                      background: "linear-gradient(180deg, var(--kd9-gold), rgba(75,0,130,.75))",
                    }}
                    title={`${row.date}: ${total} eventos operativos`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] mt-2 text-white/45">
              <span>{r.activity_daily?.[0]?.date || range.from}</span>
              <span>{r.activity_daily?.[r.activity_daily.length - 1]?.date || range.to}</span>
            </div>
          </section>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6" data-testid="analytics-governance">
            <Metric icon={Users} label="Solicitudes de rol" value={r.governance?.pending_role_requests} />
            <Metric icon={ShoppingBag} label="Productos por decidir" value={r.governance?.pending_products} />
            <Metric icon={Sparkles} label="Diagnósticos revisión final" value={r.governance?.diagnostics_final_review} />
            <Metric icon={WalletCards} label="Retiros pendientes" value={r.governance?.pending_withdrawals} />
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5 mb-6" data-testid="analytics-operations">
            <OperationCard
              icon={Sparkles}
              title="Diagnósticos"
              primary={ops.diagnostics?.count}
              primaryLabel="Solicitudes"
              energy={ops.diagnostics?.energy}
              statuses={ops.diagnostics?.statuses}
            />
            <OperationCard
              icon={BookOpen}
              title="Academia"
              primary={ops.academy?.enrollments}
              primaryLabel="Matrículas"
              energy={ops.academy?.energy}
              statuses={ops.academy?.statuses}
            />
            <OperationCard
              icon={ShoppingBag}
              title="Marketplace"
              primary={ops.marketplace?.orders}
              primaryLabel="Pedidos"
              energy={ops.marketplace?.energy}
              statuses={ops.marketplace?.statuses}
            />
            <OperationCard
              icon={Heart}
              title="Acompañamientos"
              primary={ops.accompaniment?.requests}
              primaryLabel="Solicitudes"
              energy={ops.accompaniment?.energy}
              statuses={ops.accompaniment?.statuses}
            />
            <OperationCard
              icon={Briefcase}
              title="Servicios de Consultor"
              primary={ops.consultant_services?.requests}
              primaryLabel="Solicitudes"
              energy={ops.consultant_services?.energy}
              statuses={ops.consultant_services?.statuses}
            />
            <OperationCard
              icon={Sparkles}
              title="Oráculo"
              primary={ops.oracle?.consultations}
              primaryLabel="Consultas"
              energy={ops.oracle?.energy}
              statuses={ops.oracle?.statuses}
            />
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <Metric icon={WalletCards} label="Energía acreditada" value={r.energy?.credited} suffix=" KD" />
            <Metric icon={WalletCards} label="Energía debitada" value={r.energy?.debited} suffix=" KD" />
            <Metric icon={ShoppingBag} label="Comisión Marketplace" value={ops.marketplace?.commission_energy} suffix=" KD" />
            <Metric icon={Radio} label="Comisión Ofrendas" value={r.offerings?.kd9_commission} suffix=" KD" />
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            <Breakdown title="Usuarios por rol" values={r.users?.roles} />
            <Breakdown title="Servicios de Consultor" values={ops.consultant_services?.kinds} />
            <Breakdown title="Soporte por estado" values={ops.support?.statuses} />
            <Breakdown title="Auditoría por módulo" values={r.audit?.domains} />
            <Breakdown title="Retiros por estado" values={r.withdrawals?.statuses} />
            <Breakdown title="Pagos KD por estado" values={r.service_payments} />
          </div>

          <div className="kd9-card mt-5 grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <small>Contenido activo</small>
              <b className="block kd9-gold-text">{number(r.content?.diagnostics_active)} diagnósticos</b>
            </div>
            <div>
              <small>Academia publicada</small>
              <b className="block kd9-gold-text">{number(r.content?.courses_active)} cursos</b>
            </div>
            <div>
              <small>Marketplace publicado</small>
              <b className="block kd9-gold-text">{number(r.content?.products_active)} productos</b>
            </div>
            <div>
              <small>Salud de trazabilidad</small>
              <b className="block kd9-gold-text">
                {number(r.audit?.integrity?.invalid)} alertas · {number(r.audit?.notification_failures)} fallos
              </b>
            </div>
          </div>

          <p className="mt-5 text-xs text-white/45">
            {r.disclaimer} La Analítica Global es de solo lectura; las decisiones se ejecutan en sus centros de gobierno correspondientes.
          </p>
        </>
      )}
    </PageShell>
  );
}
